import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { getFirestoreDB } from "@/firebase";

const FEATURE_FLAG_COLLECTION = "trialFinderFeatureFlags";
const FEATURE_FLAG_DOC = "featureFlags"; // single doc with all flags

// in-memory cache
let cachedFeatureFlags: Record<string, boolean> | null = null;
let fetchPromise: Promise<Record<string, boolean>> | null = null;

/**
 * Fetches all feature flags once and caches them.
 */
async function getFeatureFlags(): Promise<Record<string, boolean>> {
    if (cachedFeatureFlags) {
        return cachedFeatureFlags;
    }

    if (!fetchPromise) {
        const db = getFirestoreDB();
        const featureFlagRef = doc(db, FEATURE_FLAG_COLLECTION, FEATURE_FLAG_DOC);

        fetchPromise = getDoc(featureFlagRef)
            .then((docSnap) => {
                if (docSnap.exists()) {
                    cachedFeatureFlags = docSnap.data() as Record<string, boolean>;
                } else {
                    cachedFeatureFlags = {};
                }
                return cachedFeatureFlags;
            })
            .catch((err) => {
                console.error("Error fetching feature flags:", err);
                cachedFeatureFlags = {};
                return cachedFeatureFlags;
            });
    }

    return fetchPromise;
}

/**
 * Generic hook for any boolean feature flag.
 */
function useFeatureFlag(flagName: string) {
    const [isLoading, setIsLoading] = useState(true);
    const [isEnabled, setIsEnabled] = useState<boolean>(false);

    useEffect(() => {
        getFeatureFlags()
            .then((flags) => {
                setIsEnabled(Boolean(flags[flagName]));
            })
            .finally(() => setIsLoading(false));
    }, [flagName]);

    return { isLoading, isEnabled };
}

/**
 * Specific hooks
 */
export function useSponsorFeatureFlag() {
    return useFeatureFlag("showSponsoredStudies");
}

export function useBetaSurveyFeatureFlag() {
    return useFeatureFlag("showBetaSurvey");
}

export function useChatBotFeatureFlag() {
    return useFeatureFlag("showChatBot");
}

export function useTrialGPTFeatureFlag() {
    return useFeatureFlag("showTrialGPT");
}

export function useEnableBetaProtection() {
    return useFeatureFlag("enableBetaProtection");
}

export function useEnrolledStudiesFeatureFlag() {
    return useFeatureFlag("enableEnrolledStudies");
}

export function useUserRegistryFeatureFlag() {
    return useFeatureFlag("enableUserRegistry");
}
